import { Link } from 'react-router-dom';
import Seo from '../../components/Seo';
import { useAsync } from '../../lib/useAsync';
import { dashboardApi } from '../../api/resources';
import { Stat, PageLoader, ErrorState } from '../../components/ui';
import { formatDate, relativeTime } from '../../lib/format';

export default function AdminDashboard() {
  const analytics = useAsync(() => dashboardApi.analytics(), []);
  const activity = useAsync(() => dashboardApi.recentActivity(10), []);

  if (analytics.loading) return <PageLoader />;
  if (analytics.error) return <ErrorState error={analytics.error} onRetry={analytics.reload} />;

  const a = analytics.data || {};
  const totals = a.totals || {};
  const pending = a.pending_review || {};
  const byStatus = a.stories_by_status || {};

  return (
    <div className="space-y-8">
      <Seo title="Admin Dashboard" />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold">Dashboard</h1>
          <p className="text-ink-muted">Overview of content and community activity.</p>
        </div>
        <Link to="/admin/approvals" className="btn-primary">
          Approval Center
        </Link>
      </div>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
        <Stat value={totals.stories ?? 0} label="Stories" icon="📰" />
        <Stat value={totals.events ?? 0} label="Events" icon="📅" />
        <Stat value={totals.activities ?? 0} label="Activities" icon="🤝" />
        <Stat value={totals.members ?? 0} label="Members" icon="👥" />
        <Stat value={totals.announcements ?? 0} label="Announcements" icon="📢" />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Pending review */}
        <div className="card p-6">
          <h2 className="mb-4 text-lg font-semibold text-ink">Pending Review</h2>
          <div className="space-y-3">
            {Object.entries(pending).map(([key, count]) => (
              <div key={key} className="flex items-center justify-between rounded-lg bg-surface-alt px-4 py-3">
                <span className="capitalize text-ink-soft">{key}</span>
                <span className={count > 0 ? 'pill-gold' : 'pill bg-surface-gray text-ink-muted'}>
                  {count}
                </span>
              </div>
            ))}
          </div>
          <Link to="/admin/approvals" className="btn-outline mt-4 w-full">
            Go to Approval Center
          </Link>
        </div>

        {/* Stories by status */}
        <div className="card p-6">
          <h2 className="mb-4 text-lg font-semibold text-ink">Stories by Status</h2>
          <div className="space-y-3">
            {Object.entries(byStatus).map(([status, count]) => {
              const total = Object.values(byStatus).reduce((x, y) => x + y, 0) || 1;
              return (
                <div key={status}>
                  <div className="mb-1 flex justify-between text-sm">
                    <span className="capitalize text-ink-soft">{status}</span>
                    <span className="font-medium text-ink">{count}</span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-surface-gray">
                    <div className="h-full rounded-full bg-maroon-700" style={{ width: `${(count / total) * 100}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Upcoming events + recent activity */}
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="card p-6">
          <h2 className="mb-4 text-lg font-semibold text-ink">Upcoming Events</h2>
          {a.upcoming_events?.length ? (
            <ul className="divide-y divide-cream-300">
              {a.upcoming_events.map((e) => (
                <li key={e.id} className="flex items-center justify-between py-3">
                  <div>
                    <p className="font-medium text-ink">{e.title}</p>
                    <p className="text-xs text-ink-muted">{e.location || 'TBA'}</p>
                  </div>
                  <span className="text-sm text-ink-muted">{formatDate(e.starts_at)}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="py-6 text-center text-sm text-ink-muted">No upcoming events.</p>
          )}
        </div>

        <div className="card p-6">
          <h2 className="mb-4 text-lg font-semibold text-ink">Recent Activity</h2>
          {activity.loading ? (
            <div className="space-y-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="skeleton h-8 w-full rounded" />
              ))}
            </div>
          ) : activity.data?.length ? (
            <ul className="space-y-3 text-sm">
              {activity.data.map((log) => (
                <li key={log.id} className="flex items-start gap-3">
                  <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-gold-400" />
                  <div>
                    <p className="text-ink-soft">{log.description || log.action}</p>
                    <p className="text-xs text-ink-muted">
                      {log.user ? `${log.user} · ` : ''}
                      {relativeTime(log.created_at)}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="py-6 text-center text-sm text-ink-muted">No recent activity.</p>
          )}
        </div>
      </div>
    </div>
  );
}
